package com.rosario.juridico.platform

import com.rosario.juridico.marketplace.model.Abogado
import com.rosario.juridico.marketplace.model.Consulta
import com.rosario.juridico.marketplace.model.Propuesta
import com.rosario.juridico.marketplace.model.Expediente
import com.rosario.juridico.marketplace.model.Movimiento
import com.rosario.juridico.marketplace.model.Tarea
import com.rosario.juridico.marketplace.model.NivelVerificacion
import kotlinx.coroutines.flow.Flow
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.Instant
import kotlinx.datetime.Clock
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerialName

expect object FirebaseProxy {
    fun getCurrentUserId(): String?
    fun getUserEmail(): String?
    fun isAnonymous(): Boolean
    fun isLoggedIn(): Boolean
    fun isEmailVerified(): Boolean
    suspend fun reloadUser(): Boolean
    suspend fun sendEmailVerification(): AuthResult
    suspend fun signInAnonymously(): String?
    suspend fun getIdToken(): String?
    fun getAccountCreationTimestamp(): Long

    // Autenticación Real con manejo de estados
    suspend fun signIn(email: String, password: String): AuthResult
    suspend fun signUp(email: String, password: String): AuthResult
    suspend fun signInWithGoogle(idToken: String): AuthResult
    suspend fun sendPasswordResetEmail(email: String): AuthResult
    suspend fun logout()

    suspend fun getConsultas(tipoConsulta: String? = null): List<Consulta>
    suspend fun getConsultasPendientes(tipoConsulta: String? = null): List<Consulta>
    suspend fun aprobarConsulta(consultaId: String)
    suspend fun rechazarConsulta(consultaId: String, motivo: String)
    suspend fun eliminarConsulta(consultaId: String)

    suspend fun getPropuestas(consultaId: String): List<Propuesta>
    suspend fun getAbogado(uid: String): Abogado?
    suspend fun getAbogadosDestacados(): List<Abogado>

    suspend fun saveAbogado(abogado: Abogado)
    suspend fun updateSubscription(uid: String, subscribed: Boolean)
    suspend fun addConsulta(consulta: Consulta)
    suspend fun addPropuesta(propuesta: Propuesta)
    suspend fun aceptarPropuesta(consultaId: String)
    suspend fun initializeUserIfNeeded(uid: String)
    suspend fun startTrial(uid: String)
    suspend fun consumeFreeUse(uid: String)
    suspend fun checkAndConsumeUsage(uid: String): AccessResult

    fun observeUserStatus(): Flow<UserStatus>
    fun observeAuthState(): Flow<String?>
    
    // Pagos y Retornos
    fun handlePaymentReturn(onSuccess: () -> Unit)
    
    // Cloud Functions Gen 2
    suspend fun callFunction(name: String, data: Map<String, Any?>): String?
    suspend fun canjearPromo(codigo: String): PromoResult

    // Agenda de Expedientes
    suspend fun getExpedientes(userId: String): List<Expediente>
    suspend fun getTareas(expedienteId: String): List<Tarea>
    suspend fun addExpediente(userId: String, expediente: Expediente): String
    suspend fun updateExpediente(userId: String, expediente: Expediente)
    suspend fun addTarea(expedienteId: String, tarea: Tarea): String
    suspend fun updateExpedienteArchivado(userId: String, expedienteId: String, archivado: Boolean)
    suspend fun eliminarExpediente(userId: String, expedienteId: String)

    // Movimientos procesales
    suspend fun getMovimientos(userId: String, expedienteId: String): List<Movimiento>
    suspend fun addMovimiento(userId: String, expedienteId: String, movimiento: Movimiento): String
    suspend fun updateMovimiento(userId: String, expedienteId: String, movimiento: Movimiento)
    suspend fun eliminarMovimiento(userId: String, expedienteId: String, movimientoId: String)
}

@Serializable
data class UserQuota(
    @SerialName("initial_uses_remaining") val initial_uses_remaining: Int = 5,
    @SerialName("monthly_uses_remaining") val monthly_uses_remaining: Int = 3,
    @SerialName("last_monthly_use_timestamp") val last_monthly_use_timestamp: Long? = null
)

@Serializable
data class UserFirestoreDoc(
    @SerialName("uid") val uid: String = "",
    @SerialName("rol") val rol: String = "indefinido",
    @SerialName("nivelVerificacion") val nivelVerificacion: String = NivelVerificacion.NIVEL_1_CREADA.name,
    @SerialName("premium") val premium: Boolean = false,
    @SerialName("isAdmin") val isAdmin: Boolean = false,
    @SerialName("initial_uses_remaining") val initial_uses_remaining: Int = 5,
    @SerialName("monthly_uses_remaining") val monthly_uses_remaining: Int = 3,
    @SerialName("last_monthly_use_timestamp") val last_monthly_use_timestamp: Long? = null,
    @SerialName("trialExpiresAt") val trialExpiresAt: Long = 0L,
    @SerialName("vencimiento") val vencimiento: Long = 0L
)

enum class AccessResult { ALLOWED_INITIAL, ALLOWED_MONTHLY, DENIED }

fun checkAccess(quota: UserQuota): AccessResult {
    // REGLA A: Usos de bienvenida
    if (quota.initial_uses_remaining > 0) {
        return AccessResult.ALLOWED_INITIAL
    }

    // REGLA B: Uso mensual (Ciclo de 30 días para 3 usos)
    val lastUse = quota.last_monthly_use_timestamp
    val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
    val thirtyDaysMillis = 30L * 24 * 60 * 60 * 1000

    // Si pasaron 30 días (o es la primera vez), el ciclo se reinicia (otorga 3 usos)
    if (lastUse == null || (now - lastUse) >= thirtyDaysMillis) {
        return AccessResult.ALLOWED_MONTHLY
    }
    
    // Si no pasaron 30 días, verificamos si aún tiene usos del cupo mensual actual
    if (quota.monthly_uses_remaining > 0) {
        return AccessResult.ALLOWED_MONTHLY
    }

    // REGLA C: Límite alcanzado
    return AccessResult.DENIED
}

data class UserStatus(
    val isInitialized: Boolean = false,
    val isLoading: Boolean = false,
    val isPremium: Boolean = false,
    val isAdmin: Boolean = false,
    val rol: String = "indefinido",
    val nivelVerificacion: String = NivelVerificacion.NIVEL_1_CREADA.name,
    val initial_uses_remaining: Int = 5,
    val monthly_uses_remaining: Int = 3,
    val last_monthly_use_timestamp: Long? = null,
    val premiumExpiresAt: Long = 0L,
    val trialExpiresAt: Long = 0L,
    val consistency: com.rosario.juridico.premium.EntitlementConsistencyState = com.rosario.juridico.premium.EntitlementConsistencyState.LOCAL_ONLY
) {
    val gracePeriod: Boolean get() = consistency == com.rosario.juridico.premium.EntitlementConsistencyState.GRACE_PERIOD
}

sealed class AuthResult {
    data class Success(val uid: String) : AuthResult()
    data class Error(val message: String, val code: AuthErrorCode) : AuthResult()
}

enum class AuthErrorCode {
    EMAIL_ALREADY_IN_USE,
    WRONG_PASSWORD,
    USER_NOT_FOUND,
    INVALID_EMAIL,
    INVALID_CREDENTIALS,
    NETWORK_ERROR,
    UNKNOWN
}

sealed class PromoResult {
    data object Success : PromoResult()
    data class Error(val message: String) : PromoResult()
}
